import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Box,
	FormControlLabel,
	Grid,
	IconButton,
	Radio,
	RadioGroup,
	Typography,
} from "@mui/material";
import { addDays } from "date-fns";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { DateRangePicker } from "rsuite";

import ControlledDatePicker from "../controlled/ControlledDatePicker";
import ControlledLocationAutocomplete from "../controlled/ControlledLocationAutocomplete";
import ControlledRadioGroup from "../controlled/ControlledRadioGroup";
import ControlledTextField from "../controlled/ControlledTextField";

import { useTranslation } from "react-i18next";
import SetValueButton from "../SetValueButton";
import { StyledDateRangePicker } from "../StyledDateRangePicker";
import { StyledTimePicker } from "../StyledTimePicker";

const { beforeToday, combine, allowedMaxDays } = DateRangePicker;

export const LoadingPartRanges = ({
	control,
	watch,
	errors,
	setValue,
	resetField,
	resetAutocompleteKey,
	addressDefaultValue,
}) => {
	const { t } = useTranslation();
	const today = new Date();
	const [dateType, setDateType] = useState("day");

	const watchLoadingDateType = watch("loadingDateType");
	const watchUnloadingDateType = watch("unloadingDateType");

	useEffect(() => {
		setDateType(watchLoadingDateType);

		if (
			watchLoadingDateType === "range" ||
			watchUnloadingDateType === "range"
		) {
			resetField("loadingRepeat");
			setValue("loadingRepeat", "");
		}
	}, [watchLoadingDateType, watchUnloadingDateType]);

	return (
		<Grid
			container
			rowSpacing={0}
			columnSpacing={1}
			sx={{ py: 1, px: 2, position: "relative" }}
		>
			<Box
				sx={{
					position: "absolute",
					top: "-12px",
					left: "98px",
					background: "#fff",
					border: "1px solid #7f7f7f",
					height: "24px",
					width: "156px",
					display: "flex",
					alignItems: "center",
				}}
			>
				<Controller
					control={control}
					name="loadingDateType"
					defaultValue="day"
					render={({ field: { onChange, value } }) => (
						<RadioGroup
							row
							name="loadingDateType"
							value={value}
							onChange={(e) => {
								onChange(e);
								setDateType((prev) => (prev === "day" ? "range" : "day"));
							}}
						>
							<FormControlLabel
								sx={{ ml: "11px", mr: "22px" }}
								value="day"
								control={
									<Radio
										sx={{
											padding: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label={t("day")}
							/>
							<FormControlLabel
								sx={{ mr: 0 }}
								value="range"
								control={
									<Radio
										sx={{
											padding: 0,
											mr: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label={t("range_of_days")}
							/>
						</RadioGroup>
					)}
				/>
			</Box>
			<Grid
				size={{
					lg: 6,
				}}
			>
				<ControlledLocationAutocomplete
					resetKey={resetAutocompleteKey}
					label={t("postal_code_city_country_label")}
					control={control}
					margin="normal"
					name="loadingAddress"
					defaultValue={addressDefaultValue}
					InputLabelProps={{ shrink: true }}
					setFormValue={setValue}
					errors={errors}
				/>
			</Grid>
			{dateType === "day" && (
				<>
					<Grid
						size={{
							lg: 3,
						}}
					>
						<Box
							sx={{
								height: "100%",
								width: "100%",
								alignItems: "start",
								display: "flex",
								justifyContent: "center",
								mt: "8px",
							}}
						>
							<Box sx={{ width: "100%", position: "relative" }}>
								<Typography
									component="p"
									sx={{
										color: "rgba(0, 0, 0, 0.6)",
										textAlign: "center",
										fontSize: "0.8rem",
										mb: "2px",
									}}
								>
									{t("next_days")}
								</Typography>
								<ControlledRadioGroup
									disabled={
										watchUnloadingDateType === "range" || dateType === "range"
									}
									className="radio-repeating-days"
									control={control}
									margin="normal"
									name="loadingRepeat"
									label={t("next_days")}
									options={[
										{ value: 1, label: "+1" },
										{ value: 2, label: "+2" },
										{ value: 3, label: "+3" },
									]}
								/>
								<Box
									sx={{
										position: "absolute",
										right: "9px",
										top: "-4px",
									}}
								>
									<IconButton
										sx={{ p: "5px" }}
										type="button"
										onClick={() => resetField("loadingRepeat")}
									>
										<RefreshIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Box>
							</Box>
						</Box>
					</Grid>
					<Grid
						size={{
							lg: 3,
						}}
					>
						<Box
							sx={{
								alignItems: "center",
								display: "flex",
								position: "relative",
							}}
						>
							<ControlledDatePicker
								control={control}
								margin="normal"
								name="loadingDate"
								label={t("date")}
								error={!!errors?.loadingDate}
							/>
							<Box
								sx={{
									position: "absolute",
									top: "4px",
									left: "72px",
									display: "flex",
									gap: "6px",
								}}
							>
								<SetValueButton
									title={t("today")}
									style={{ padding: "2px 5px" }}
									type="button"
									onClick={() =>
										setValue("loadingDate", today, {
											shouldValidate: true,
										})
									}
								>
									{t("today_short")}
								</SetValueButton>
								<SetValueButton
									title={t("tomorrow")}
									style={{ padding: "2px 5px" }}
									type="button"
									onClick={() =>
										setValue("loadingDate", addDays(today, 1), {
											shouldValidate: true,
										})
									}
								>
									{t("tomorrow_short")}
								</SetValueButton>
								<SetValueButton
									title={t("after_tomorrow")}
									style={{ padding: "2px 5px" }}
									type="button"
									onClick={() =>
										setValue("loadingDate", addDays(today, 2), {
											shouldValidate: true,
										})
									}
								>
									{t("after_tomorrow_short")}
								</SetValueButton>
							</Box>
						</Box>
					</Grid>
				</>
			)}
			{dateType === "range" && (
				<Grid
					size={{
						lg: 6,
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							position: "relative",
							marginTop: "16px",
							marginBottom: "8px",
						}}
					>
						<Controller
							control={control}
							name="loadingDateRange"
							// defaultValue="day"
							render={({ field: { onChange, value } }) => (
								<StyledDateRangePicker
									name="loadingDateRange"
									value={value}
									onChange={onChange}
									error={!!errors?.loadingDateRange}
									shouldDisableDate={combine(allowedMaxDays(25), beforeToday())}
								/>
							)}
						/>
					</Box>
				</Grid>
			)}
			<Grid
				size={{
					lg: 9,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledTextField
						control={control}
						margin="normal"
						name="privateNote"
						defaultValue=""
						label={t("private_note_label")}
						error={!!errors?.privateNote}
						multiline={true}
						maxRows={1}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 3,
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						position: "relative",
						gap: "4px",
						height: "100%",
					}}
				>
					<Controller
						control={control}
						name="loadingTimeStart"
						render={({ field: { onChange, value } }) => {
							return (
								<StyledTimePicker
									label={t("hours_label") + " *"}
									name="loadingTimeStart"
									value={value || null}
									onChange={onChange}
									error={!!errors?.loadingTimeStart}
								/>
							);
						}}
					/>
					<span>-</span>
					<Controller
						control={control}
						name="loadingTimeEnd"
						// defaultValue="day"
						render={({ field: { onChange, value } }) => {
							return (
								<StyledTimePicker
									label={t("hours_label")}
									name="loadingTimeEnd"
									value={value || null}
									onChange={onChange}
									error={!!errors?.loadingTimeEnd}
								/>
							);
						}}
					/>
				</Box>
			</Grid>
		</Grid>
	);
};
